import React, { useEffect, useRef, useState } from "react";
import { BarChart3, Clock, Layers, Loader2, Package } from "lucide-react";
import { toast } from "react-toastify";
import { fetchProductionActivity } from "../../api/ProductionAnalyticsAPIs";

const PRESETS = [
  { id: "today", label: "اليوم" },
  { id: "yesterday", label: "أمس" },
  { id: "date", label: "تاريخ محدد" },
  { id: "range", label: "فترة مخصصة" },
];

const inputClass =
  "px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500";

const formatCount = (value) => Number(value || 0).toLocaleString("en-US");

const formatAverage = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (Number.isInteger(n)) return formatCount(n);
  return n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const to12HourClock = (value) => {
  if (!value || /[صم]|AM|PM/i.test(value)) return value;
  return String(value).replace(/\b([01]?\d|2[0-3]):([0-5]\d)\b/g, (_, hourText, minute) => {
    const hour = Number(hourText);
    const suffix = hour < 12 ? "ص" : "م";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute} ${suffix}`;
  });
};

const batchLabel = (item) => {
  const display = Number(item?.displayBatchNumber);
  const absolute = Number(item?.batchNumber);
  const n = display > 0 ? display : absolute;
  return Number.isFinite(n) ? `#${n}` : "—";
};

const SummaryCard = ({ label, value, hint, icon: Icon, accent }) => (
  <div className="relative bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl border border-slate-200/80 px-2 py-2 md:p-4 shadow-sm">
    <div className="absolute top-1.5 left-1.5 md:top-3 md:left-3 p-1 md:p-2 rounded-md md:rounded-xl bg-slate-100 text-slate-500">
      <Icon className="w-3 h-3 md:w-4 md:h-4" />
    </div>
    <p className="text-[11px] md:text-sm text-slate-500 leading-snug pl-6 md:pl-9">{label}</p>
    <p className={`text-base md:text-2xl font-bold mt-0.5 md:mt-1.5 leading-none ${accent}`}>{value}</p>
    {hint ? <p className="text-[10px] md:text-xs text-slate-500 mt-1 leading-snug">{hint}</p> : null}
  </div>
);

const ProductionAnalyticsSection = ({ refreshKey = 0 }) => {
  const [preset, setPreset] = useState("today");
  const [date, setDate] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const generation = useRef(0);

  const filtersReady =
    preset === "today" ||
    preset === "yesterday" ||
    (preset === "date" && Boolean(date)) ||
    (preset === "range" && Boolean(from) && Boolean(to));

  const query = {
    preset,
    from: preset === "date" ? date : preset === "range" ? from : "",
    to: preset === "range" ? to : "",
    fromTime,
    toTime,
  };

  useEffect(() => {
    if (!filtersReady) {
      setLoading(false);
      setItems([]);
      setSummary(null);
      setHasMore(false);
      setError("");
      return undefined;
    }

    const gen = ++generation.current;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchProductionActivity({ ...query, page: 1 });
        if (cancelled || gen !== generation.current) return;
        setSummary(data);
        setItems(data.items || []);
        setPage(data.page || 1);
        setHasMore(Boolean(data.hasMore));
      } catch (err) {
        if (cancelled || gen !== generation.current) return;
        setSummary(null);
        setItems([]);
        setHasMore(false);
        setError(err.message || "تعذر تحميل تحليل الإنتاج");
      } finally {
        if (!cancelled && gen === generation.current) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // query is rebuilt from the same fields listed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, date, from, to, fromTime, toTime, refreshKey, filtersReady]);

  const showMore = async () => {
    if (!filtersReady || loadingMore || !hasMore) return;
    const gen = generation.current;
    setLoadingMore(true);
    try {
      const data = await fetchProductionActivity({ ...query, page: page + 1 });
      if (gen !== generation.current) return;
      setItems((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        const next = (data.items || []).filter((row) => !seen.has(row.id));
        return [...prev, ...next];
      });
      setPage(data.page || page + 1);
      setHasMore(Boolean(data.hasMore));
    } catch (err) {
      if (gen === generation.current) {
        toast.error(err.message || "تعذر تحميل المزيد");
      }
    } finally {
      if (gen === generation.current) setLoadingMore(false);
    }
  };

  const last = summary?.lastBatch;

  const filters = (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5 md:gap-2">
        {PRESETS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setPreset(option.id)}
            className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-lg md:rounded-xl text-xs md:text-sm font-semibold border transition ${
              preset === option.id
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-2 md:gap-3">
        {preset === "date" && (
          <label className="text-[11px] md:text-xs text-slate-500">
            التاريخ
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={`mt-1 block ${inputClass}`}
            />
          </label>
        )}
        {preset === "range" && (
          <>
            <label className="text-[11px] md:text-xs text-slate-500">
              من تاريخ
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className={`mt-1 block ${inputClass}`}
              />
            </label>
            <label className="text-[11px] md:text-xs text-slate-500">
              إلى تاريخ
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className={`mt-1 block ${inputClass}`}
              />
            </label>
          </>
        )}
        <label className="text-[11px] md:text-xs text-slate-500">
          من وقت
          <input
            type="time"
            value={fromTime}
            onChange={(e) => setFromTime(e.target.value)}
            className={`mt-1 block ${inputClass}`}
          />
        </label>
        <label className="text-[11px] md:text-xs text-slate-500">
          إلى وقت
          <input
            type="time"
            value={toTime}
            onChange={(e) => setToTime(e.target.value)}
            className={`mt-1 block ${inputClass}`}
          />
        </label>
        {(fromTime || toTime) && (
          <button
            type="button"
            onClick={() => {
              setFromTime("");
              setToTime("");
            }}
            className="px-2.5 py-1.5 md:px-3 md:py-2 rounded-lg md:rounded-xl text-xs md:text-sm font-medium text-slate-600 hover:bg-slate-50 border border-slate-200"
          >
            مسح الوقت
          </button>
        )}
      </div>
    </div>
  );

  return (
    <section className="bg-white/90 backdrop-blur rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
      <div className="px-3 py-3 md:px-5 md:py-4 border-b border-slate-100">
        <h2 className="text-base md:text-lg font-bold text-slate-800">تحليل الإنتاج</h2>
        <p className="text-xs md:text-sm text-slate-500 mt-0.5">
          بتوقيت مصر
          {summary?.periodLabel ? (
            <>
              {" — "}
              <span dir="ltr">{to12HourClock(summary.periodLabel)}</span>
            </>
          ) : (
            " — الدفعات المكتملة خلال الفترة"
          )}
        </p>
      </div>

      <div className="p-3 md:p-5 space-y-3 md:space-y-4">
        {!filtersReady ? (
          <p className="text-sm text-slate-500 text-center py-6">اختار التاريخ لعرض التحليل</p>
        ) : loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 md:h-24 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 text-center py-6">{error}</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
              <SummaryCard
                label="إجمالي الباتشات"
                value={formatCount(summary?.totalBatches)}
                icon={Layers}
                accent="text-indigo-600"
              />
              <SummaryCard
                label="إجمالي القطع"
                value={formatCount(summary?.totalPieces)}
                icon={Package}
                accent="text-emerald-600"
              />
              <SummaryCard
                label="متوسط القطع لكل Batch"
                value={formatAverage(summary?.averagePiecesPerBatch)}
                icon={BarChart3}
                accent="text-slate-800"
              />
              <SummaryCard
                label="آخر Batch مكتمل"
                value={last ? batchLabel(last) : "—"}
                hint={
                  last ? (
                    <span>
                      {formatCount(last.pieceCount)} قطعة ·{" "}
                      <span dir="ltr">{last.completedDate}</span>
                      {" · "}
                      <span dir="ltr">{to12HourClock(last.completedTime)}</span>
                    </span>
                  ) : (
                    "لا توجد دفعة في هذه الفترة"
                  )
                }
                icon={Clock}
                accent="text-slate-800"
              />
            </div>
        )}

        {filters}

        {filtersReady && !loading && !error && (
          <>
            <div className="border border-slate-200 rounded-2xl overflow-x-auto">
              <table className="w-full text-xs md:text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-2 md:p-3 text-right font-semibold text-slate-600">Batch</th>
                    <th className="p-2 md:p-3 text-right font-semibold text-slate-600">القطع</th>
                    <th className="p-2 md:p-3 text-right font-semibold text-slate-600">التاريخ</th>
                    <th className="p-2 md:p-3 text-right font-semibold text-slate-600">الوقت</th>
                    <th className="p-2 md:p-3 text-right font-semibold text-slate-600">المصدر</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-t border-slate-100">
                      <td className="p-2 md:p-3 font-semibold text-slate-800">{batchLabel(item)}</td>
                      <td className="p-2 md:p-3 tabular-nums text-slate-700">{formatCount(item.pieceCount)}</td>
                      <td className="p-2 md:p-3 text-slate-700" dir="ltr">{item.completedDate}</td>
                      <td className="p-2 md:p-3 tabular-nums text-slate-700" dir="ltr">{to12HourClock(item.completedTime)}</td>
                      <td className="p-2 md:p-3 text-slate-600">{item.productionSource || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {items.length === 0 && (
                <p className="p-8 text-center text-sm text-slate-400">لا توجد دفعات مكتملة في هذه الفترة</p>
              )}
            </div>

            {hasMore && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={showMore}
                  disabled={loadingMore}
                  className="px-5 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 rounded-xl transition disabled:opacity-50"
                >
                  {loadingMore ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> جاري التحميل...
                    </span>
                  ) : (
                    "إظهار المزيد"
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ProductionAnalyticsSection;
